/**
 * Prayer rug groups in the cart (cart drawer and cart page).
 *
 * A rug and its lines share the hidden `_hdy_lien` property (written at add to cart by
 * assets/hadiyati-tapis-options.js); `_hdy_supplement` gives the type of each added line:
 * options "perso", "carte", "emballage", "prioritaire", or "complement". Lines without `_hdy_lien`
 * (older carts, other products) are left alone.
 *
 * Every change below is sent to Shopify first; the cart is then re-rendered from Shopify's answer
 * (rendered sections), so a price or a choice is never shown before Shopify confirmed it.
 * - Removing the rug removes its whole group in one request.
 * - The rug quantity drives its options (same configuration, n copies), in one request.
 *   Complements keep their own quantity and can be removed on their own (theme behaviour).
 * - Removing the personalisation or the card removes the paid line AND the matching properties
 *   of the rug (word and colour, or message), so what is sent to Shopify matches what is paid.
 * - Safety net after any cart change: options left without their rug are removed; their
 *   quantity follows the rug; a personalisation or card still described on the rug but no longer
 *   charged is added back.
 * - The cart bubble counts products (rugs, complements, other products), not the rug options.
 *
 * While a change is running, the cart is marked busy, further clicks are ignored and checkout is
 * disabled. On a refusal or a network error, an alert is shown and the real cart is reloaded.
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'hadiyati-cart-addons';
/** Option types: follow the rug quantity, not counted as products. */
const OPTION_TYPES = new Set(['perso', 'carte', 'emballage', 'prioritaire', 'express', 'symbole']);
/** Lines that are not physical products (options and the order-wide express service). */
const SERVICE_TYPES = new Set(['perso', 'carte', 'emballage', 'prioritaire', 'express', 'symbole']);
/** Names of the personalisation symbols, by key. */
const SYMBOL_LABELS = { coeur: 'Cœur', lune: 'Lune', etoile: 'Étoile' };

/**
 * Symbols recorded on a rug: « 1:coeur|2:etoile » → [{ rank: '1', key: 'coeur' }, …].
 * @param {any} rug
 */
function symbolsOf(rug) {
  return String(props(rug)._hdy_symboles || '')
    .split('|')
    .map((entry) => entry.split(':'))
    .filter(([rank, key]) => rank && SYMBOL_LABELS[/** @type {keyof typeof SYMBOL_LABELS} */ (key)])
    .map(([rank, key]) => ({ rank, key }));
}
/** Rug properties written by each option, when the rug does not list them (older carts). */
const FALLBACK_FIELDS = {
  perso: (/** @type {string} */ key) => /^mot\b|couleur/i.test(key),
  carte: (/** @type {string} */ key) => /message/i.test(key),
};

let busy = false;
let syncing = false;

/**
 * Cart routes of the shop. snippets/scripts.liquid declares them with `const Theme` (a global
 * binding, not a window property), so they are read as `Theme`, with Shopify's standard routes
 * as fallback.
 */
function cartRoutes() {
  // @ts-ignore Theme is a global constant declared by snippets/scripts.liquid
  const theme = typeof Theme === 'undefined' ? null : Theme;
  // @ts-ignore Shopify is set by Shopify on every storefront page
  const root = window.Shopify?.routes?.root || '/';
  return {
    cart_url: theme?.routes?.cart_url || `${root}cart`,
    cart_change_url: theme?.routes?.cart_change_url || `${root}cart/change`,
    cart_update_url: theme?.routes?.cart_update_url || `${root}cart/update`,
    cart_add_url: theme?.routes?.cart_add_url || `${root}cart/add.js`,
  };
}

/**
 * JSON request to the cart API; a refusal (e.g. not enough stock) becomes an Error carrying
 * Shopify's own message.
 * @param {string} url
 * @param {RequestInit} [init]
 */
async function request(url, init) {
  const response = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...init,
  });
  const data = await response.json().catch(() => null);
  if (!response.ok || data?.status >= 400 || data?.errors) {
    const message = data?.description || data?.message || (typeof data?.errors === 'string' ? data.errors : '');
    throw new Error(message || `${response.status}`);
  }
  return data;
}

const getCart = () => request(`${cartRoutes().cart_url}.js`);

/** @param {string} url @param {Record<string, unknown>} body */
const post = (url, body) => request(url, { method: 'POST', body: JSON.stringify(body) });

/** Section ids of the cart drawer / cart page currently on the page. */
function cartSectionIds() {
  return [...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
    .filter(Boolean)
    .join(',');
}

/** @param {any} item */
const props = (item) => item.properties || {};

/** @param {Record<string, number>} updates @param {any} [cart] - Cart before the change */
const toLines = (updates, cart) =>
  Object.entries(updates).map(([id, quantity]) => ({
    id,
    // The event wants a positive quantity: for a removed line, the quantity that was removed.
    quantity: quantity || cart?.items.find((/** @type {any} */ item) => item.key === id)?.quantity || 1,
  }));

/**
 * Lines of one rug group.
 * @param {any} cart
 * @param {string} link
 */
function groupOf(cart, link) {
  const lines = cart.items.filter((/** @type {any} */ item) => props(item)._hdy_lien === link);
  return {
    rug: lines.find((/** @type {any} */ item) => !props(item)._hdy_supplement),
    addons: lines.filter((/** @type {any} */ item) => props(item)._hdy_supplement),
  };
}

/**
 * Re-renders the cart drawer / cart page and the cart icon from a confirmed Shopify answer.
 * @param {any} cart - Cart JSON returned by Shopify
 * @param {Record<string, string> | undefined} sections - Rendered sections, when requested
 * @param {'update' | 'remove' | 'add'} action
 * @param {{ id: string, quantity: number }[]} [lines] - Changed lines (the event requires at least one)
 */
function publish(cart, sections, action, lines = []) {
  const changed = lines.length
    ? lines
    : cart.items.length
      ? cart.items.map((/** @type {any} */ item) => ({ id: item.key, quantity: item.quantity }))
      : [{ id: 'cart', quantity: 1 }];
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent(/** @type {any} */ ({ action, context: 'cart', lines: changed, promise: deferred.promise }))
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { sections, items: cart.items, itemCount: cart.item_count, source: SOURCE },
  });
}

/** Rendered sections of the current cart (used after an error, to show the real cart). */
async function reloadSections() {
  const ids = cartSectionIds();
  const cart = await getCart();
  let sections;
  if (ids) {
    const response = await fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids)}`, {
      credentials: 'same-origin',
    });
    if (response.ok) sections = await response.json();
  }
  publish(cart, sections, 'update');
}

/** @param {boolean} on */
function setBusy(on) {
  busy = on;
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (on) element.setAttribute('aria-busy', 'true');
    else element.removeAttribute('aria-busy');
    element.classList.toggle('cart-items-disabled', on);
  }
  setCheckoutLocked(on);
}

/** @param {boolean} locked */
function setCheckoutLocked(locked) {
  for (const button of document.querySelectorAll('button[name="checkout"], [name="checkout"]')) {
    const control = /** @type {HTMLButtonElement} */ (button);
    if (locked) {
      control.dataset.hdyLocked = 'true';
      control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
    } else if (control.dataset.hdyLocked) {
      delete control.dataset.hdyLocked;
      control.disabled = false;
      control.removeAttribute('aria-disabled');
    }
  }
}

/**
 * Message at the top of each cart list, announced to screen readers. Written after the cart has
 * been re-rendered, so the re-render does not remove it.
 * @param {string} message
 */
function showAlert(message) {
  setTimeout(() => {
    for (const cartItems of document.querySelectorAll('cart-items-component')) {
      cartItems.querySelector('.hdy-cart-alert')?.remove();
      const alert = document.createElement('p');
      alert.className = 'hdy-cart-alert';
      alert.setAttribute('role', 'alert');
      alert.textContent = message;
      const list = cartItems.querySelector('.cart-items__wrapper') || cartItems;
      list.prepend(alert);
    }
  }, 400);
}

/**
 * Runs one cart change: busy state, one change at a time, re-render from Shopify's answer;
 * on failure, alert + real cart reloaded (nothing is retried, so nothing can be added twice).
 * @param {() => Promise<{ cart: any, sections?: Record<string, string>, action: 'update' | 'remove' | 'add', lines?: { id: string, quantity: number }[] }>} change
 */
async function run(change) {
  if (busy) return;
  setBusy(true);
  try {
    const { cart, sections, action, lines } = await change();
    publish(cart, sections, action, lines);
  } catch (error) {
    console.warn(`[${SOURCE}]`, error);
    // Shopify's own refusal message (e.g. stock) is shown; a network failure gets a plain sentence.
    const network = error instanceof TypeError;
    const reason = network
      ? ' Vérifiez votre connexion.'
      : error instanceof Error && error.message && !/^\d+$/.test(error.message)
        ? ` ${error.message}`
        : '';
    let reloaded = true;
    try {
      await reloadSections();
    } catch {
      reloaded = false;
    }
    showAlert(
      `La modification n’a pas pu être enregistrée.${reason} ${
        reloaded ? 'Le panier affiché a été actualisé.' : 'Rechargez la page pour afficher votre panier à jour.'
      }`
    );
  } finally {
    setBusy(false);
  }
}

/** Removes a rug and every line of its group, in one request. @param {string} key */
function removeGroup(key) {
  return run(async () => {
    const cart = await getCart();
    const rug = cart.items.find((/** @type {any} */ item) => item.key === key);
    if (!rug) throw new Error('Article introuvable.');
    const link = props(rug)._hdy_lien;
    /** @type {Record<string, number>} */
    const updates = { [key]: 0 };
    if (link) for (const addon of groupOf(cart, link).addons) updates[addon.key] = 0;
    const result = await post(`${cartRoutes().cart_update_url}.js`, { updates, sections: cartSectionIds() });
    return { cart: result, sections: result.sections, action: 'remove', lines: toLines(updates, cart) };
  });
}

/**
 * Rug quantity: the rug and its options take the same quantity, in one request.
 * @param {string} key
 * @param {number} quantity
 */
function setRugQuantity(key, quantity) {
  return run(async () => {
    const cart = await getCart();
    const rug = cart.items.find((/** @type {any} */ item) => item.key === key);
    if (!rug) throw new Error('Article introuvable.');
    const target = Math.max(1, quantity);
    /** @type {Record<string, number>} */
    const updates = { [key]: target };
    for (const addon of groupOf(cart, props(rug)._hdy_lien).addons) {
      if (OPTION_TYPES.has(props(addon)._hdy_supplement)) updates[addon.key] = target;
    }
    const result = await post(`${cartRoutes().cart_update_url}.js`, { updates, sections: cartSectionIds() });
    // Shopify may limit a quantity to the stock: the cart shown is the one it kept.
    const kept = result.items.find((/** @type {any} */ item) => props(item)._hdy_lien === props(rug)._hdy_lien && !props(item)._hdy_supplement);
    if (kept && kept.quantity !== target) {
      showAlert(`Seulement ${kept.quantity} exemplaire(s) disponible(s) pour ce tapis.`);
    }
    return { cart: result, sections: result.sections, action: 'update', lines: toLines(updates) };
  });
}

/**
 * Removes one option of a rug. For the personalisation and the card, the rug's matching
 * properties are removed too (first the paid line, then the properties: if the second step
 * fails, the safety net charges the option again, so nothing is ever given for free).
 * @param {string} key - Key of the option line
 */
function removeOption(key) {
  return run(async () => {
    const routes = cartRoutes();
    const cart = await getCart();
    const option = cart.items.find((/** @type {any} */ item) => item.key === key);
    if (!option) throw new Error('Article introuvable.');
    const type = props(option)._hdy_supplement;
    const { rug } = groupOf(cart, props(option)._hdy_lien);

    if (type === 'symbole' && rug) {
      // Paid line first, then the rug's record of its symbols (if the second step fails, the safety
      // net charges the symbol again, so nothing is ever given for free).
      await post(`${routes.cart_change_url}.js`, { id: key, quantity: 0 });
      const rank = props(option)._hdy_rang;
      const kept = symbolsOf(rug).filter((entry) => entry.rank !== rank);
      const rugProps = { ...props(rug) };
      if (kept.length) {
        rugProps.Symboles = kept.map((entry) => SYMBOL_LABELS[/** @type {keyof typeof SYMBOL_LABELS} */ (entry.key)]).join(', ');
        rugProps._hdy_symboles = kept.map((entry) => `${entry.rank}:${entry.key}`).join('|');
      } else {
        delete rugProps.Symboles;
        delete rugProps._hdy_symboles;
        delete rugProps._hdy_symbole;
        delete rugProps._hdy_champs_symboles;
      }
      const result = await post(`${routes.cart_change_url}.js`, { id: rug.key, quantity: rug.quantity, properties: rugProps, sections: cartSectionIds() });
      return { cart: result, sections: result.sections, action: 'update', lines: [{ id: key, quantity: option.quantity }, { id: rug.key, quantity: rug.quantity }] };
    }

    if ((type !== 'perso' && type !== 'carte') || !rug) {
      const result = await post(`${routes.cart_change_url}.js`, { id: key, quantity: 0, sections: cartSectionIds() });
      return { cart: result, sections: result.sections, action: 'remove', lines: [{ id: key, quantity: option.quantity }] };
    }

    await post(`${routes.cart_change_url}.js`, { id: key, quantity: 0 });
    if (type === 'perso') {
      // The symbols follow the word: they leave with the personalisation.
      const symbolLines = groupOf(cart, props(option)._hdy_lien).addons.filter((line) => props(line)._hdy_supplement === 'symbole');
      if (symbolLines.length) {
        /** @type {Record<string, number>} */
        const gone = {};
        for (const line of symbolLines) gone[line.key] = 0;
        await post(`${routes.cart_update_url}.js`, { updates: gone });
      }
    }

    const rugProps = { ...props(rug) };
    const listed = String(rugProps[type === 'perso' ? '_hdy_champs_perso' : '_hdy_champs_carte'] || '')
      .split('|')
      .filter(Boolean);
    for (const name of Object.keys(rugProps)) {
      const written = listed.length ? listed.includes(name) : !name.startsWith('_') && FALLBACK_FIELDS[type](name);
      if (written) delete rugProps[name];
    }
    if (type === 'perso') {
      delete rugProps.Symboles;
      for (const name of String(rugProps._hdy_champs_symboles || '').split('|').filter(Boolean)) delete rugProps[name];
      delete rugProps._hdy_symboles;
      delete rugProps._hdy_symbole;
      delete rugProps._hdy_champs_symboles;
    }
    delete rugProps[type === 'perso' ? '_hdy_perso' : '_hdy_carte'];
    delete rugProps[type === 'perso' ? '_hdy_champs_perso' : '_hdy_champs_carte'];

    let result = await post(`${routes.cart_change_url}.js`, {
      id: rug.key,
      quantity: rug.quantity,
      properties: rugProps,
      ...(type === 'perso' ? {} : { sections: cartSectionIds() }),
    });

    if (type === 'perso') {
      // The other lines of the group name the rug with its word (« Pour le tapis : … – Amina »):
      // without the personalisation, they name it by its title only, also at checkout.
      const label = rug.product_title;
      for (const addon of groupOf(result, props(rug)._hdy_lien).addons) {
        if (!props(addon)['Pour le tapis'] || props(addon)['Pour le tapis'] === label) continue;
        await post(`${routes.cart_change_url}.js`, {
          id: addon.key,
          quantity: addon.quantity,
          properties: { ...props(addon), 'Pour le tapis': label },
        });
      }
      result = await post(`${routes.cart_update_url}.js`, { updates: {}, sections: cartSectionIds() });
    }
    return { cart: result, sections: result.sections, action: 'update', lines: [{ id: key, quantity: option.quantity }, { id: rug.key, quantity: rug.quantity }] };
  });
}

/**
 * Safety net after any cart change (also on page load): options without their rug are removed,
 * options follow the rug quantity, a personalisation or card still described on the rug is
 * charged. Complements keep their own quantity; a complement whose rug is gone is removed.
 */
async function syncCart() {
  if (syncing || busy) return;
  syncing = true;
  setCheckoutLocked(true);
  try {
    const routes = cartRoutes();
    const cart = await getCart();

    /** @type {Map<string, any>} */
    const rugs = new Map();
    for (const item of cart.items) {
      const p = props(item);
      if (p._hdy_lien && !p._hdy_supplement && !rugs.has(p._hdy_lien)) rugs.set(p._hdy_lien, item);
    }

    /** @type {Record<string, number>} */
    const updates = {};
    const present = new Set();
    for (const item of cart.items) {
      const p = props(item);
      if (!p._hdy_supplement || !p._hdy_lien) continue;
      present.add(p._hdy_supplement === 'symbole' ? `${p._hdy_lien}|symbole|${p._hdy_rang}` : `${p._hdy_lien}|${p._hdy_supplement}`);
      const rug = rugs.get(p._hdy_lien);
      if (!rug) updates[item.key] = 0;
      else if (OPTION_TYPES.has(p._hdy_supplement) && item.quantity !== rug.quantity) updates[item.key] = rug.quantity;
    }

    const missing = [];
    for (const [link, rug] of rugs) {
      const p = props(rug);
      const word = Object.entries(p).find(([key]) => !key.startsWith('_') && /^mot\b/i.test(key))?.[1];
      const forRug = [rug.product_title, word].filter(Boolean).join(' – ');
      for (const [type, variant] of [
        ['perso', p._hdy_perso],
        ['carte', p._hdy_carte],
      ]) {
        if (variant && !present.has(`${link}|${type}`)) {
          missing.push({ id: Number(variant), quantity: rug.quantity, properties: { 'Pour le tapis': forRug, _hdy_lien: link, _hdy_supplement: type } });
        }
      }
    }

    // Express (whole order, no rug link): one line, quantity 1, only while a physical product remains.
    const expressLines = cart.items.filter((/** @type {any} */ item) => props(item)._hdy_supplement === 'express');
    const physical = cart.items.some((/** @type {any} */ item) => !SERVICE_TYPES.has(props(item)._hdy_supplement));
    expressLines.forEach((/** @type {any} */ line, /** @type {number} */ index) => {
      if (!physical || index > 0) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
    });

    // Symbols still described on the rug (« Symboles ») but no longer charged: charged again.
    for (const [link, rug] of rugs) {
      const variant = props(rug)._hdy_symbole;
      if (!variant) continue;
      const word = Object.entries(props(rug)).find(([key]) => !key.startsWith('_') && /^mot\b/i.test(key))?.[1];
      const forRug = [rug.product_title, word].filter(Boolean).join(' – ');
      for (const entry of symbolsOf(rug)) {
        if (present.has(`${link}|symbole|${entry.rank}`)) continue;
        missing.push({
          id: Number(variant),
          quantity: rug.quantity,
          properties: { 'Pour le tapis': forRug, _hdy_lien: link, _hdy_supplement: 'symbole', _hdy_rang: entry.rank, Symbole: SYMBOL_LABELS[/** @type {keyof typeof SYMBOL_LABELS} */ (entry.key)] },
        });
      }
    }

    if (!Object.keys(updates).length && !missing.length) return;

    const sections = cartSectionIds();
    let result;
    if (Object.keys(updates).length) {
      result = await post(`${routes.cart_update_url}.js`, { updates, ...(missing.length ? {} : { sections }) });
    }
    if (missing.length) {
      await post(routes.cart_add_url, { items: missing });
      result = await post(`${routes.cart_update_url}.js`, { updates: {}, sections });
    }
    if (result) publish(result, result.sections, Object.keys(updates).length ? 'update' : 'add', toLines(updates, cart));
  } catch (error) {
    console.warn(`[${SOURCE}]`, error);
  } finally {
    syncing = false;
    if (!busy) setCheckoutLocked(false);
  }
}

/**
 * Cart bubble count without the rug options (same rule as snippets/hadiyati-cart-count.liquid).
 * The theme's cart icon writes the cart's total quantity after each update; this runs after it.
 */
async function fixCartBubble() {
  try {
    // Always the full cart: an "add" event only lists the added lines.
    const { items } = await getCart();
    const count = items.reduce(
      (/** @type {number} */ sum, /** @type {any} */ item) =>
        OPTION_TYPES.has(props(item)._hdy_supplement) ? sum : sum + item.quantity,
      0
    );
    for (const output of document.querySelectorAll('.cart-bubble__text-count')) {
      if (output.textContent !== String(count)) output.textContent = count < 100 ? String(count) : '';
    }
    sessionStorage.setItem('cart-count', JSON.stringify({ value: String(count), timestamp: Date.now() }));
  } catch {
    // The theme's own count stays displayed.
  }
}

/** @param {EventTarget | null} target */
const rowOf = (target) =>
  /** @type {HTMLElement | null} */ (target instanceof Element ? target.closest('tr.hdy-cart-row') : null);

// Remove buttons of a rug group. Capture on window: runs before the theme's own handler.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element ? event.target.closest('.cart-items__remove') : null;
    const row = rowOf(button);
    if (!button || !row?.dataset.key) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    if (row.classList.contains('hdy-cart-row--express')) {
      // Order-wide express service: only this line is removed (and it is not added back by itself).
      run(async () => {
        const result = await post(`${cartRoutes().cart_change_url}.js`, { id: row.dataset.key, quantity: 0, sections: cartSectionIds() });
        return { cart: result, sections: result.sections, action: 'remove', lines: [{ id: row.dataset.key || '', quantity: 1 }] };
      });
    } else if (row.classList.contains('hdy-cart-row--rug')) removeGroup(row.dataset.key);
    else if (row.classList.contains('hdy-cart-row--complement')) {
      // Complement: only this line is removed; the rug and its other lines stay.
      run(async () => {
        const result = await post(`${cartRoutes().cart_change_url}.js`, { id: row.dataset.key, quantity: 0, sections: cartSectionIds() });
        return { cart: result, sections: result.sections, action: 'remove', lines: [{ id: row.dataset.key || '', quantity: 1 }] };
      });
    } else removeOption(row.dataset.key);
  },
  { capture: true }
);

// Rug quantity selector: drives the rug and its options together (debounced like the theme).
/** @type {ReturnType<typeof setTimeout> | undefined} */
let quantityTimer;
window.addEventListener(
  'quantity-selector:update',
  (event) => {
    const row = rowOf(event.target);
    if (!row?.dataset.key || !row.classList.contains('hdy-cart-row--rug')) return;
    event.stopImmediatePropagation();
    const quantity = Number(/** @type {any} */ (event).detail?.quantity) || 1;
    const key = row.dataset.key;
    clearTimeout(quantityTimer);
    setCheckoutLocked(true);
    quantityTimer = setTimeout(() => setRugQuantity(key, quantity), 300);
  },
  { capture: true }
);

// Any cart change (theme or this script): checkout locked until it is confirmed, then the
// bubble count is corrected and the safety net runs (not after this script's own changes).
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const promise = /** @type {any} */ (event).promise;
  if (!promise) return;
  setCheckoutLocked(true);
  promise
    .then((/** @type {any} */ result) => {
      setTimeout(fixCartBubble, 0);
      if (result?.detail?.source !== SOURCE) syncCart();
      else syncExpress();
    })
    .catch(() => {})
    .finally(() => {
      if (!busy && !syncing) setTimeout(() => setCheckoutLocked(false), 0);
    });
});

/**
 * Order-wide express service. Added once, after a product add succeeded (event from the product
 * page); never twice: one request at a time, and the real cart is checked right before adding.
 * @param {string} variant
 */
let addingExpress = false;
async function addExpress(variant) {
  if (addingExpress || !variant) return;
  addingExpress = true;
  try {
    const routes = cartRoutes();
    const cart = await getCart();
    const has = cart.items.some((/** @type {any} */ item) => props(item)._hdy_supplement === 'express');
    const physical = cart.items.some((/** @type {any} */ item) => !SERVICE_TYPES.has(props(item)._hdy_supplement));
    if (has || !physical) return;
    await post(routes.cart_add_url, { items: [{ id: Number(variant), quantity: 1, properties: { _hdy_supplement: 'express' } }] });
    const result = await post(`${routes.cart_update_url}.js`, { updates: {}, sections: cartSectionIds() });
    publish(result, result.sections, 'add', [{ id: 'express', quantity: 1 }]);
  } catch (error) {
    console.warn(`[${SOURCE}]`, error);
  } finally {
    addingExpress = false;
  }
}

/** Express safety net after this script's own changes (the rest runs in syncCart). */
async function syncExpress() {
  if (syncing || busy || addingExpress) return;
  try {
    const cart = await getCart();
    const lines = cart.items.filter((/** @type {any} */ item) => props(item)._hdy_supplement === 'express');
    if (!lines.length) return;
    const physical = cart.items.some((/** @type {any} */ item) => !SERVICE_TYPES.has(props(item)._hdy_supplement));
    /** @type {Record<string, number>} */
    const updates = {};
    lines.forEach((/** @type {any} */ line, /** @type {number} */ index) => {
      if (!physical || index > 0) updates[line.key] = 0;
      else if (line.quantity !== 1) updates[line.key] = 1;
    });
    if (!Object.keys(updates).length) return;
    const result = await post(`${cartRoutes().cart_update_url}.js`, { updates, sections: cartSectionIds() });
    publish(result, result.sections, 'remove', toLines(updates, cart));
  } catch (error) {
    console.warn(`[${SOURCE}]`, error);
  }
}

document.addEventListener('hdy:express-add', (event) => {
  addExpress(/** @type {CustomEvent} */ (event).detail?.variant);
});

// The shopper unticked the express switch: the service leaves the order (and is not added back by itself).
document.addEventListener('hdy:express-remove', () => {
  run(async () => {
    const cart = await getCart();
    /** @type {Record<string, number>} */
    const updates = {};
    for (const item of cart.items) if (props(item)._hdy_supplement === 'express') updates[item.key] = 0;
    if (!Object.keys(updates).length) {
      // Nothing to remove: show the cart as it is.
      return { cart, sections: undefined, action: 'update', lines: [] };
    }
    const result = await post(`${cartRoutes().cart_update_url}.js`, { updates, sections: cartSectionIds() });
    return { cart: result, sections: result.sections, action: 'remove', lines: toLines(updates, cart) };
  });
});

// Cart page or a cart edited in another tab: check once after load.
if (document.readyState === 'complete') {
  syncCart();
} else {
  window.addEventListener('load', () => syncCart(), { once: true });
}

// Message of a personalised card: one line with « Voir plus », or the whole text with « Voir moins ».
// The text is only truncated visually (CSS); each message keeps its own state, kept when the cart
// is refreshed (the line keeps its key).
/** @type {Set<string>} */
const openMessages = new Set();
const watchedMessages = new WeakSet();
const messageResizer = new ResizeObserver((entries) => {
  for (const entry of entries) updateMessage(/** @type {HTMLElement} */ (entry.target));
});

/** @param {HTMLElement} box */
function updateMessage(box) {
  const text = box.querySelector('.hdy-cart-message__text');
  const button = /** @type {HTMLButtonElement | null} */ (box.querySelector('.hdy-cart-message__toggle'));
  if (!text || !button) return;
  const open = openMessages.has(box.dataset.hdyMessageKey || '');
  if (box.hasAttribute('data-open') !== open) box.toggleAttribute('data-open', open);
  // Needed when the message does not fit on one line (or has line breaks); never while open.
  const needed = open || /\n/.test(text.textContent || '') || text.scrollWidth > text.clientWidth + 1;
  if (button.hidden === needed) button.hidden = !needed;
  const label = open ? 'Voir moins' : 'Voir plus';
  if (button.textContent !== label) button.textContent = label;
  if (button.getAttribute('aria-expanded') !== String(open)) button.setAttribute('aria-expanded', String(open));
}

function refreshMessages() {
  for (const box of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-hdy-message]'))) {
    updateMessage(box);
    if (!watchedMessages.has(box)) {
      watchedMessages.add(box);
      messageResizer.observe(box);
    }
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element ? event.target.closest('.hdy-cart-message__toggle') : null;
  const box = /** @type {HTMLElement | null} */ (button?.closest('[data-hdy-message]'));
  if (!button || !box) return;
  event.preventDefault();
  const key = box.dataset.hdyMessageKey || '';
  if (openMessages.has(key)) openMessages.delete(key);
  else openMessages.add(key);
  for (const other of /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('[data-hdy-message]'))) {
    if (other.dataset.hdyMessageKey === key) updateMessage(other);
  }
});

let messageFrame = 0;
new MutationObserver(() => {
  cancelAnimationFrame(messageFrame);
  messageFrame = requestAnimationFrame(refreshMessages);
}).observe(document.body, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['hidden', 'aria-expanded', 'data-open'],
});
refreshMessages();
